function waitForGtmDomAndLoad(timeout = 4000): Promise<void> {
  return new Promise((resolve) => {
    const hasBoth = () => {
      const dl = window.dataLayer || []
      return (
        dl.some((e) => e.event === 'gtm.dom') &&
        dl.some((e) => e.event === 'gtm.load')
      )
    }
    if (hasBoth()) return resolve()

    let elapsed = 0
    const interval = 50
    const timer = setInterval(() => {
      elapsed += interval
      if (hasBoth() || elapsed > timeout) {
        clearInterval(timer)
        resolve()
      }
    }, interval)
  })
}

export class EventQueue {
  private readonly queue: Record<string, any>[] = []
  private isFlushing = false
  private enabled = false

  /**
   * Set the enabled state of the queue.
   */
  async setEnabled(enabled: boolean) {
    this.enabled = enabled
    if (enabled) {
      await waitForGtmDomAndLoad()
      this.flush()
    }
  }

  /**
   * Enqueue an event to be pushed to the data layer.
   * @param event - The event object to enqueue.
   */
  enqueue(event: Record<string, any>): void {
    this.queue.push(event)
    this.flush()
  }

  /**
   * Flushes the queue by pushing events to the `window.dataLayer`.
   */
  private flush(): void {
    if (!this.canFlush()) return

    if (!this.isFlushing) {
      this.isFlushing = true

      while (this.queue.length > 0) {
        const event = this.queue.shift()
        try {
          window.dataLayer.push(event)
        } catch (error) {
          console.error('Error pushing event to dataLayer:', error, event)
        }
      }

      this.isFlushing = false
    }
  }

  /**
   * Determines if the queue can be flushed.
   * @returns `true` if `window.dataLayer` is available, otherwise `false`.
   */
  private canFlush(): boolean {
    return this.enabled && typeof window !== 'undefined' && !!window.dataLayer
  }

  /**
   * Manually force a flush (useful when the dataLayer becomes available later).
   */
  forceFlush(): void {
    this.flush()
  }

  /**
   * Get the current queue for debugging or testing.
   * @returns The array of queued events.
   */
  getQueue(): Record<string, any>[] {
    return [...this.queue]
  }
}

export const eventQueue = new EventQueue()
